import { adminProfileSchema, type AdminProfile } from '@steado/admin-contract/auth';
import { z } from 'zod';

/**
 * Where a signed-in operator's session lives.
 *
 * **sessionStorage, not localStorage.** The console can display medical
 * conditions, medications and full coach transcripts, and the refresh token is
 * valid for days. Persisting that across browser restarts leaves a long-lived
 * credential where any XSS can read it; scoping it to the tab means closing the
 * browser ends the session. The cost is re-authenticating after a restart,
 * which for an internal tool is the right trade.
 */
export const SESSION_STORAGE_KEY = 'steado_admin_session';

export const storedSessionSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
  admin: adminProfileSchema,
});

export type StoredSession = z.infer<typeof storedSessionSchema>;

export type SessionStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * Anything unparseable is discarded rather than repaired. A half-valid session
 * would produce confusing failures much later, on a request that has nothing to
 * do with the corruption.
 */
export function readSession(storage: SessionStorage): StoredSession | null {
  let raw: string | null;
  try {
    raw = storage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }

  if (raw === null) return null;

  try {
    const parsed = storedSessionSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function writeSession(storage: SessionStorage, session: StoredSession): void {
  try {
    storage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Storage being unavailable degrades persistence, not the session itself.
  }
}

export function clearSession(storage: SessionStorage): void {
  try {
    storage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // Nothing to do — the in-memory session is dropped by the caller regardless.
  }
}

/** Initials for the avatar chip, e.g. "Neha Raghavan" → "NR". */
export function initialsOf(admin: Pick<AdminProfile, 'displayName' | 'email'>): string {
  const name = admin.displayName?.trim() ?? '';

  if (name !== '') {
    const parts = name.split(/\s+/).filter((part) => part !== '');
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
    const initials = `${first}${last}`;
    if (initials !== '') return initials.toUpperCase();
  }

  const local = admin.email.split('@')[0] ?? '';
  return (local.slice(0, 2) || '?').toUpperCase();
}
